import { PurchaseFormulaFields } from 'src/tables/PurchaseTable/PurchaseTable.types';
import {
  evaluateFormulaBase,
  FormulaEditable,
  FormulaExecutable,
  fromExecutableFormula,
  parseEditableFormulaBase,
  parseExecutableFormula,
  tagFieldNameToKey,
  tagKeyToFieldName,
  validateFormulaSingleStatement,
} from 'src/utils/columns/customColumnUtils';
import { PurchaseOrderTicketGroup, Tag, UiMoney } from 'src/WebApiController';

import { PURCHASE_FORMULA_FIELDS_TO_CID } from '../../constants/contentIdMaps';
import { ColumnPersonalizationFlags } from '../columnUtils.types';
import {
  getPersonalizationFlagsFromFormulaBase,
  MathematicalToken,
  parseMathematicalTokensFromFormulaBase,
} from '../customColumnMathUtils';
import { PURCHASES_TABLE_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES } from './purchasesColumnUtils.constants';
import { PurchasesTableColumnId } from './purchasesColumnUtils.types';
import { CustomPurchasesColumn } from './purchasesCustomColumnUtils.types';

export const PURCHASE_TABLE_CUSTOM_COLUMN_PERSONALIZATION_CONFIG: Partial<ColumnPersonalizationFlags> =
  {
    isHiddenByDefault: true,
    isNumeric: true,
  };

export const FORMULA_FIELD_TO_COLUMN_ID: Record<
  keyof PurchaseFormulaFields,
  PurchasesTableColumnId
> = {
  ['ticketCount']: PurchasesTableColumnId.Tickets,
  ['unitCost']: PurchasesTableColumnId.UnitCost,
  ['totalCost']: PurchasesTableColumnId.TotalCost,
  ['totalPayment']: PurchasesTableColumnId.TotalPayment,
};

export const parseEditableFormula = (
  editableFormula: FormulaEditable | undefined
): string[] | undefined => {
  const parsedTokenResult = parseEditableFormulaBase(editableFormula);
  if (!parsedTokenResult) {
    return;
  }

  const tokens: string[] = [];
  parsedTokenResult.forEach((tokenResult) => {
    if (tokenResult.isPlainToken) {
      tokens.push(tokenResult.token);
    } else {
      const fieldName = tokenResult.token;
      if (
        PURCHASE_FORMULA_FIELDS_TO_CID[
          fieldName as keyof PurchaseFormulaFields
        ] == null
      ) {
        // Tag field
        tokens.push(tagFieldNameToKey(fieldName));
        return;
      }
      tokens.push(
        PURCHASE_FORMULA_FIELDS_TO_CID[fieldName as keyof PurchaseFormulaFields]
      );
    }
  });

  return tokens;
};

export const filterCustomColumnsForPurchase = (
  customColumns: CustomPurchasesColumn[],
  tagsForEntityType: Tag[] | undefined
): CustomPurchasesColumn[] => {
  const purchaseFormulaFields = Object.keys(PURCHASE_FORMULA_FIELDS_TO_CID);
  const tagKeys = tagsForEntityType?.map((t) => tagKeyToFieldName(t.key)) ?? [];
  return customColumns.filter((column) => {
    const editableFormula = fromExecutableFormula(column.formula);
    if (!editableFormula) return false;

    const fieldNamesUsed =
      parseEditableFormulaBase(editableFormula, true)?.map((t) => t.token) ??
      [];

    return fieldNamesUsed.every(
      (f) => purchaseFormulaFields.includes(f) || tagKeys.includes(f)
    );
  });
};

const mapPurchaseFormulaFields = (
  purchase?: PurchaseOrderTicketGroup
): PurchaseFormulaFields => {
  const tagFields = purchase?.tags?.reduce((acc, tag) => {
    const numericValue = parseFloat(tag.value);
    if (isNaN(numericValue)) return acc;

    return {
      ...acc,
      [tagKeyToFieldName(tag.key)]: numericValue,
    };
  }, {});

  return {
    ticketCount: purchase?.ticketCnt,
    unitCost: purchase?.unitCst?.amt,
    totalCost: purchase?.totalCst?.amt,
    totalPayment: purchase?.totalPmt?.amt,
    ...tagFields,
  };
};

export const evaluateFormula = (
  formula: FormulaExecutable,
  purchase?: PurchaseOrderTicketGroup
) => {
  const data = mapPurchaseFormulaFields(purchase);

  return evaluateFormulaBase(formula, data);
};

export const PURCHASE_FORMULA_TEST_DATA: PurchaseOrderTicketGroup = {
  ticketCnt: 1,
  totalPmt: { amt: 1 } as UiMoney,
  totalCst: { amt: 1 } as UiMoney,
  unitCst: { amt: 1 } as UiMoney,
} as PurchaseOrderTicketGroup;

const createPurchaseTestData = (
  tagsForEntityType: Tag[] | undefined
): PurchaseOrderTicketGroup => {
  const tagData = tagsForEntityType?.map((t) => {
    t.value = '1';
    return t;
  });

  return {
    ...PURCHASE_FORMULA_TEST_DATA,
    tags: tagData ?? [],
  };
};

export const validateFormula = (
  formula: string,
  tagsForEntityType: Tag[] | undefined
) => {
  return (
    parseExecutableFormula(formula) != null &&
    evaluateFormula(formula, createPurchaseTestData(tagsForEntityType)) !=
      null &&
    validateFormulaSingleStatement(formula)
  );
};

export const findDependentPurchaseTableColumnIds = (
  editableFormula: FormulaEditable
): PurchasesTableColumnId[] => {
  const fieldNames =
    parseEditableFormulaBase(editableFormula, true)?.map((t) => t.token) ?? [];

  return (
    Object.entries(FORMULA_FIELD_TO_COLUMN_ID)
      .filter(([key]) => fieldNames.includes(key))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .map(([_, value]) => value)
  );
};

const getTokenFromFieldName = (fieldName: string): MathematicalToken => {
  const columnId =
    FORMULA_FIELD_TO_COLUMN_ID[fieldName as keyof PurchaseFormulaFields];

  if (columnId == null) {
    // Tag field - here we assume it's not currency - might need to change
    return {
      token: `tag-${fieldName}`,
      isPlainToken: false,
      isCurrency: false,
    };
  }

  const overrides =
    PURCHASES_TABLE_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[columnId];

  return {
    token: columnId,
    isPlainToken: false,
    isCurrency: overrides?.isCurrency ?? false,
    isPercent: overrides?.isPercent ?? false,
  };
};

/**
 * Parse editableFormula, transforming formula into mathematical tokens
 * to infer personalization flags
 *
 * E.g. `${ticketCount} + 20` ==> [
 *     { token: 'ticketCount', isCurrency: false, isPercent: false, isPlainToken: false },
 *     { token: '+', isCurrency: false, isPercent: false, isPlainToken: true },
 *     { token: '20', isCurrency: false, isPercent: false, isPlainToken: true }
 * ]
 * @param editableFormula
 * @returns parsed list of tokens, or undefined if failed to parse
 */
const parseMathematicalTokensFromFormula = (
  editableFormula: FormulaEditable | undefined
): MathematicalToken[] | undefined => {
  return parseMathematicalTokensFromFormulaBase(
    editableFormula,
    getTokenFromFieldName
  );
};

export const getPersonalizationFlagsFromFormula = (
  executableFormula: FormulaExecutable
): Partial<ColumnPersonalizationFlags> => {
  const editableFormula = fromExecutableFormula(executableFormula);
  const mathTokens = parseMathematicalTokensFromFormula(editableFormula);

  return getPersonalizationFlagsFromFormulaBase(mathTokens);
};
